import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  RefreshCw,
  ArrowLeft,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Filter,
} from "lucide-react";
import {
  queryActivityLog,
  fetchActivitySummary,
  ActivityQueryResponse,
  ActivitySummaryResponse,
  ActivityFilter,
  ActivityLog,
  ActivityCategory,
  ActivityStatus,
} from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { MonitoringLayout } from "@/pages/monitoring/layout";

const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const formatDuration = (ms?: number) => {
  if (!ms) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const getStatusBadge = (status: ActivityStatus) => {
  switch (status) {
    case "success":
      return (
        <Badge className="bg-green-500 hover:bg-green-600">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Success
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    case "queued":
      return (
        <Badge variant="secondary">
          <Clock className="h-3 w-3 mr-1" />
          Queued
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const getActionLabel = (action: string) => {
  const labels: Record<string, string> = {
    // SQL Sync actions
    start_sync: "Start Sync",
    pause: "Pause",
    resume: "Resume",
    cancel: "Cancel",
    detect_changes: "Detect Changes",
    reset: "Reset",
    full_resync: "Full Resync",
    batch_complete: "Batch Complete",
    // SharePoint actions
    start_job: "Start Job",
    items_found: "Items Found",
    batch_progress: "Batch Progress",
    site_complete: "Site Complete",
    job_complete: "Job Complete",
  };
  return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export default function SqlSyncActivityPage() {
  const [, setLocation] = useLocation();
  const [filter, setFilter] = useState<ActivityFilter>({
    // No default category - show all activities
    limit: 50,
    offset: 0,
  });

  // Fetch activity summary
  const {
    data: summaryData,
    isLoading: summaryLoading,
  } = useQuery<ActivitySummaryResponse>({
    queryKey: ["activitySummary", 7],
    queryFn: () => fetchActivitySummary(7),
  });

  // Fetch activity log
  const {
    data: activityData,
    isLoading: activityLoading,
    isError: activityError,
    error: activityErrorObj,
    refetch: refetchActivity,
    isFetching: activityFetching,
  } = useQuery<ActivityQueryResponse>({
    queryKey: ["activityLog", filter],
    queryFn: () => queryActivityLog(filter),
  });

  const handleFilterChange = (key: keyof ActivityFilter, value: string | undefined) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
      offset: 0, // Reset pagination on filter change
    }));
  };

  const handlePageChange = (direction: "prev" | "next") => {
    setFilter((prev) => ({
      ...prev,
      offset:
        direction === "next"
          ? (prev.offset || 0) + (prev.limit || 50)
          : Math.max(0, (prev.offset || 0) - (prev.limit || 50)),
    }));
  };

  const summary = summaryData?.summary;

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view SQL Sync Activity</p>
        </div>
      </div>
    }>
    <MonitoringLayout>
    <PageWrapper>
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-blue-500" />
            <h1 className="text-2xl font-bold">Activity Log</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setLocation("/")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchActivity()}
              disabled={activityFetching}
            >
              {activityFetching ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {!summaryLoading && summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary.totalActivities}</div>
                <div className="text-sm text-muted-foreground">Total Activities (7d)</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{summary.successCount}</div>
                <div className="text-sm text-muted-foreground">Successful</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{summary.failedCount}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{summary.successRate.toFixed(1)}%</div>
                <div className="text-sm text-muted-foreground">Success Rate</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Category</label>
                <Select
                  value={filter.category || "all"}
                  onValueChange={(v) => handleFilterChange("category", v)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="SharePoint">SharePoint</SelectItem>
                    <SelectItem value="SqlSync">SQL Sync</SelectItem>
                    <SelectItem value="EntityMapping">Entity Mapping</SelectItem>
                    <SelectItem value="FieldMapping">Field Mapping</SelectItem>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Action</label>
                <Select
                  value={filter.action || "all"}
                  onValueChange={(v) => handleFilterChange("action", v)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {/* SharePoint actions */}
                    <SelectItem value="start_job">Start Job</SelectItem>
                    <SelectItem value="items_found">Items Found</SelectItem>
                    <SelectItem value="batch_progress">Batch Progress</SelectItem>
                    <SelectItem value="site_complete">Site Complete</SelectItem>
                    <SelectItem value="job_complete">Job Complete</SelectItem>
                    {/* SQL Sync actions */}
                    <SelectItem value="start_sync">Start Sync</SelectItem>
                    <SelectItem value="pause">Pause</SelectItem>
                    <SelectItem value="resume">Resume</SelectItem>
                    <SelectItem value="cancel">Cancel</SelectItem>
                    <SelectItem value="detect_changes">Detect Changes</SelectItem>
                    <SelectItem value="reset">Reset</SelectItem>
                    <SelectItem value="full_resync">Full Resync</SelectItem>
                    <SelectItem value="batch_complete">Batch Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Status</label>
                <Select
                  value={filter.status || "all"}
                  onValueChange={(v) => handleFilterChange("status", v)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="queued">Queued</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Entity Type</label>
                <Select
                  value={filter.entityType || "all"}
                  onValueChange={(v) => handleFilterChange("entityType", v)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Entities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entities</SelectItem>
                    <SelectItem value="ImageProcessing">Image Processing</SelectItem>
                    <SelectItem value="Series">Series</SelectItem>
                    <SelectItem value="Title">Title</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Job ID</label>
                <Input
                  className="w-[200px]"
                  placeholder="Filter by Job ID..."
                  value={filter.jobId || ""}
                  onChange={(e) => handleFilterChange("jobId", e.target.value || undefined)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading state */}
        {activityLoading && (
          <div className="flex items-center gap-2 text-muted-foreground p-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading activity log...
          </div>
        )}

        {/* Error state */}
        {activityError && (
          <div className="text-red-600 text-sm p-4 bg-red-50 rounded-lg">
            {(activityErrorObj as Error)?.message || "Failed to load activity log"}
          </div>
        )}

        {/* Activity Table */}
        {!activityLoading && !activityError && activityData && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Activity Log</CardTitle>
                <div className="text-sm text-muted-foreground">
                  Showing {activityData.activities.length} of {activityData.total}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">Timestamp</TableHead>
                    <TableHead className="w-[100px]">Category</TableHead>
                    <TableHead className="w-[120px]">Action</TableHead>
                    <TableHead className="w-[100px]">Entity</TableHead>
                    <TableHead className="w-[90px]">Status</TableHead>
                    <TableHead className="w-[80px]">Duration</TableHead>
                    <TableHead className="w-[80px]">Records</TableHead>
                    <TableHead>Job ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityData.activities.map((activity, idx) => (
                    <TableRow key={activity.activityId || idx}>
                      <TableCell className="font-mono text-xs">
                        {formatDate(activity.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            activity.category === 'SharePoint' ? 'border-blue-500 text-blue-700' :
                            activity.category === 'SqlSync' ? 'border-green-500 text-green-700' :
                            ''
                          }
                        >
                          {activity.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getActionLabel(activity.action)}</Badge>
                      </TableCell>
                      <TableCell>{activity.entityType || "-"}</TableCell>
                      <TableCell>{getStatusBadge(activity.status)}</TableCell>
                      <TableCell className="text-xs">
                        {formatDuration(activity.durationMs)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {activity.recordsAffected ?? "-"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {activity.jobId ? (
                          <span title={activity.jobId}>
                            {activity.jobId.substring(0, 8)}...
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {activityData.activities.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No activities found matching filters
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {activityData.total > (filter.limit || 50) && (
                <div className="flex items-center justify-between p-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Page {Math.floor((filter.offset || 0) / (filter.limit || 50)) + 1} of{" "}
                    {Math.ceil(activityData.total / (filter.limit || 50))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange("prev")}
                      disabled={(filter.offset || 0) === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange("next")}
                      disabled={
                        (filter.offset || 0) + (filter.limit || 50) >= activityData.total
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </PageWrapper>
    </MonitoringLayout>
    </ViewGuard>
  );
}
